/**
 * Lightbinder: healer (PLAN §4). Holy Paladin shape, rotation rework (docs/design/classes/lightbinder.md,
 * rotation-rework.md wins on conflicts). Mana + Holy Power (0–5, spenders cost 3).
 *
 * Rhythm: Holy Shock (2 charges) and Judgment build Holy Power and roll procs; Judgment and an
 * Infused Flash Heal shave time off Holy Shock (CDR weaving). Procs, all visible buffs that light
 * the button they empower:
 *  - Infusion of Light (2 stacks): instant +30% Flash Heal (−2 s Holy Shock) OR Greater Judgment.
 *  - Glorious Dawn: next Holy Shock +30% and +1 Holy Power.
 *  - Divine Purpose: next Word of Light / Dawnsong free and +25%.
 * Short window: Radiant Wave (30 s) grants Infusion + Holy Power. Major window: Dawnbringer (60 s)
 * opens Avenging Light (15 s): Holy Shock always Infuses, and every spender, Infused Flash, Greater
 * Judgment and Radiant Wave builds Dawnlight; key 6 turns into Sunfall, which cashes it in.
 *
 * Every random roll is a ProcSpec on one hidden passive (`lightbinder.light_engine`) with bad-luck
 * protection, one spec per source, so the miss counters are deterministic and snapshot-safe.
 */
import type { AbilityDef, AuraDef, ClassDef, EffectSpec } from '@keystone/sim';
import { ABILITY, AURA, CLASS, PASSIVE } from '../ids';
import { PLAYER_HP, baseStats } from '../balance';
import { OFF_GCD, SPELL_RANGE, allyBuff, dodge, selfBuff } from './common';
import { LIGHTBINDER_UNLOCKS } from './unlocks';

const A = ABILITY.lightbinder;

/** Smart heals: the N most injured allies within `radius` of the caster. */
const injured = (radius: number) => ({ anchor: 'caster' as const, shape: { type: 'circle' as const, radius }, affects: 'allies' as const });

// ---------------------------------------------------------------------------
// Auras (each defined once and reused, so every inline copy is identical)
// ---------------------------------------------------------------------------

/** Proc: instant +30% Flash Heal, or Greater Judgment. Consumed by whichever resolves first. */
export const INFUSION_OF_LIGHT: AuraDef = {
  id: AURA.infusionOfLight,
  name: 'Infusion of Light',
  kind: 'buff',
  durationMs: 15000,
  stacking: 'stack',
  maxStacks: 2,
  mods: [
    { stat: 'castTimePct', value: -1, abilityIds: [A.flashHeal], ignoreStacks: true },
    { stat: 'healingDonePct', value: 0.3, abilityIds: [A.flashHeal], ignoreStacks: true },
    { stat: 'damageDonePct', value: 1.0, abilityIds: [A.judgment], ignoreStacks: true },
  ],
  // One stack per Flash Heal or Judgment, after its effects (which read the aura) resolved.
  procs: [{ on: 'castResolved', abilityIds: [A.flashHeal, A.judgment], consumeStack: true, effects: [] }],
  ui: { proc: true },
};

/** Proc: the next Holy Shock is 30% stronger and grants 1 extra Holy Power. */
export const GLORIOUS_DAWN: AuraDef = {
  id: AURA.gloriousDawn,
  name: 'Glorious Dawn',
  kind: 'buff',
  durationMs: 10000,
  stacking: 'refresh',
  mods: [
    { stat: 'healingDonePct', value: 0.3, abilityIds: [A.holyShock] },
    { stat: 'damageDonePct', value: 0.3, abilityIds: [A.holyShock] },
  ],
  procs: [{ on: 'castResolved', abilityIds: [A.holyShock], consumeStack: true, effects: [] }],
  ui: { proc: true },
};

/** Proc: the next Word of Light or Dawnsong is free and 25% stronger. */
export const DIVINE_PURPOSE: AuraDef = {
  id: AURA.divinePurpose,
  name: 'Divine Purpose',
  kind: 'buff',
  durationMs: 12000,
  stacking: 'refresh',
  mods: [
    { stat: 'resourceCostPct', value: -1, abilityIds: [A.wordOfLight, A.dawnsong] },
    { stat: 'healingDonePct', value: 0.25, abilityIds: [A.wordOfLight, A.dawnsong] },
  ],
  procs: [{ on: 'castResolved', abilityIds: [A.wordOfLight, A.dawnsong], consumeStack: true, effects: [] }],
  ui: { proc: true },
};

/** Dawnbringer's 15 s window. Holy Shock glows: every Holy Shock grants Infusion of Light. */
export const AVENGING_LIGHT: AuraDef = {
  id: AURA.avengingLight,
  name: 'Avenging Light',
  kind: 'buff',
  durationMs: 15000,
  stacking: 'refresh',
  mods: [
    { stat: 'healingDonePct', value: 0.2 },
    { stat: 'damageDonePct', value: 0.2 },
    { stat: 'critChanceBonus', value: 0.3, abilityIds: [A.holyShock] },
  ],
  ui: { glow: { abilityIds: [A.holyShock] } },
};

/** Transform: while up, key 6 (Dawnbringer) casts Sunfall. */
export const SUNFALL_READY: AuraDef = {
  id: AURA.sunfallReady,
  name: 'Sunfall',
  kind: 'buff',
  durationMs: 15000,
  stacking: 'refresh',
  overrides: [{ abilityId: A.dawnbringer, with: A.sunfall }],
  ui: { proc: true },
};

/** Stack built inside Avenging Light; Sunfall heals ×(1 + 0.5 per stack) and consumes them all. */
export const DAWNLIGHT: AuraDef = {
  id: AURA.dawnlight,
  name: 'Dawnlight',
  kind: 'buff',
  durationMs: 15000,
  stacking: 'stack',
  maxStacks: 6,
  ui: { glow: { abilityIds: [A.sunfall], atStacks: 4 }, stackCounterOn: [A.sunfall] },
};

const gain = (stacks: number, aura: AuraDef): EffectSpec => ({ type: 'applyAura', to: 'self', aura, ...(stacks !== 1 ? { stacks } : {}) });
const holyPower = (amount: number): EffectSpec => ({ type: 'resource', to: 'self', resource: 'holy_power', amount });
const shockCdr = (ms: number): EffectSpec => ({ type: 'modifyCooldown', to: 'self', abilityId: A.holyShock, deltaMs: -ms });
const ifSelf = (auraId: string, then: EffectSpec[]): EffectSpec => ({ type: 'conditional', cond: { type: 'selfHasAura', auraId }, then });
/** +1 Dawnlight, only inside Avenging Light. */
export const GAIN_DAWNLIGHT: EffectSpec = ifSelf(AURA.avengingLight, [gain(1, DAWNLIGHT)]);

/**
 * The hidden passive that rolls every proc (castResolved: after the cast's effects, and after the
 * consumeStack pass, so a spent proc can re-proc on the same press). Spec order is stable: the
 * bad-luck miss counters are keyed by index.
 */
export function lightEngine(gloriousDawnChance = 0.12): AuraDef {
  return {
    id: PASSIVE.lightEngine,
    name: 'Light Engine',
    kind: 'buff',
    durationMs: 0,
    stacking: 'unique',
    flags: { hidden: true, persistThroughDeath: true },
    procs: [
      // 0. Holy Shock in Avenging Light: Infusion guaranteed.
      { on: 'castResolved', abilityIds: [A.holyShock], if: { type: 'selfHasAura', auraId: AURA.avengingLight }, effects: [gain(1, INFUSION_OF_LIGHT)] },
      // 1. Holy Shock otherwise: 25%, +12.5% per miss (guaranteed by the 7th).
      { on: 'castResolved', abilityIds: [A.holyShock], if: { type: 'selfLacksAura', auraId: AURA.avengingLight }, chance: 0.25, badLuck: { step: 0.125 }, effects: [gain(1, INFUSION_OF_LIGHT)] },
      // 2. Glorious Dawn off Holy Shock.
      { on: 'castResolved', abilityIds: [A.holyShock], chance: gloriousDawnChance, badLuck: { step: gloriousDawnChance / 2 }, effects: [gain(1, GLORIOUS_DAWN)] },
      // 3. Infusion off Judgment: 10%, +5% per miss.
      { on: 'castResolved', abilityIds: [A.judgment], chance: 0.1, badLuck: { step: 0.05 }, effects: [gain(1, INFUSION_OF_LIGHT)] },
      // 4. Divine Purpose off either spender: 15%, +7.5% per miss.
      { on: 'castResolved', abilityIds: [A.wordOfLight, A.dawnsong], chance: 0.15, badLuck: { step: 0.075 }, effects: [gain(1, DIVINE_PURPOSE)] },
    ],
  };
}

// ---------------------------------------------------------------------------
// Abilities
// ---------------------------------------------------------------------------

const flashHeal: AbilityDef = {
  id: A.flashHeal,
  name: 'Flash Heal',
  school: 'holy',
  description:
    'Efficient single-target heal. With Infusion of Light: instant, 30% stronger, and Holy Shock recharges 2 s sooner (in Avenging Light, +1 Dawnlight).',
  targeting: 'ally',
  range: SPELL_RANGE,
  cast: { type: 'cast', timeMs: 1500 },
  cost: [{ resource: 'mana', amount: 350 }],
  effects: [
    { type: 'heal', amount: { coef: 4.5, variance: 0.05 } },
    ifSelf(AURA.infusionOfLight, [shockCdr(2000), GAIN_DAWNLIGHT]),
  ],
  ui: { tags: ['heal'], color: '#f6e27a' },
};

const holyShock: AbilityDef = {
  id: A.holyShock,
  name: 'Holy Shock',
  school: 'holy',
  description:
    'Instant heal on an ally, or holy damage on an enemy. Generates 1 Holy Power. Can grant Infusion of Light (25%, guaranteed in Avenging Light) or Glorious Dawn (12%); both chances rise after each miss.',
  targeting: 'any',
  range: SPELL_RANGE,
  cooldown: { durationMs: 7500, charges: 2 },
  cost: [{ resource: 'mana', amount: 250 }],
  gain: [{ resource: 'holy_power', amount: 1 }],
  effects: [
    {
      type: 'conditional',
      cond: { type: 'targetIsEnemy' },
      then: [{ type: 'damage', school: 'holy', amount: { coef: 1.8, variance: 0.05 } }],
      else: [{ type: 'heal', amount: { coef: 3.5, variance: 0.05 } }],
    },
    ifSelf(AURA.gloriousDawn, [holyPower(1)]),
  ],
  ui: { tags: ['heal', 'builder'], color: '#f6e27a' },
};

const wordOfLight: AbilityDef = {
  id: A.wordOfLight,
  name: 'Word of Light',
  school: 'holy',
  description: 'Spend 3 Holy Power: big instant heal. Can grant Divine Purpose (15%, rises after each miss). In Avenging Light, +1 Dawnlight.',
  targeting: 'ally',
  range: SPELL_RANGE,
  cost: [{ resource: 'holy_power', amount: 3 }],
  effects: [{ type: 'heal', amount: { coef: 7.0, variance: 0.05 } }, GAIN_DAWNLIGHT],
  ui: { tags: ['heal', 'spender'], color: '#fff3b0' },
};

const judgment: AbilityDef = {
  id: A.judgment,
  name: 'Judgment',
  school: 'holy',
  description:
    'Damage filler. Generates 1 Holy Power and Holy Shock recharges 1 s sooner. Can grant Infusion of Light (10%). Greater Judgment (with Infusion): double damage, +1 more Holy Power, and heals the most injured ally (in Avenging Light, +1 Dawnlight).',
  targeting: 'enemy',
  range: 30,
  requiresFacing: true,
  cost: [{ resource: 'mana', amount: 100 }],
  gain: [{ resource: 'holy_power', amount: 1 }],
  effects: [
    { type: 'damage', school: 'holy', amount: { coef: 2.0, variance: 0.05 } },
    shockCdr(1000),
    ifSelf(AURA.infusionOfLight, [holyPower(1), { type: 'heal', to: 'area', area: injured(40), smart: 1, amount: { coef: 2.0, variance: 0.05 } }, GAIN_DAWNLIGHT]),
  ],
  ui: { tags: ['builder'], color: '#e0b84a' },
};

const dawnsong: AbilityDef = {
  id: A.dawnsong,
  name: 'Dawnsong',
  school: 'holy',
  description: 'Spend 3 Holy Power: heal the 5 most injured allies within 30 yd. Can grant Divine Purpose (15%, rises after each miss). In Avenging Light, +1 Dawnlight.',
  targeting: 'none',
  cost: [{ resource: 'holy_power', amount: 3 }],
  effects: [{ type: 'heal', to: 'area', area: injured(30), smart: 5, amount: { coef: 3.2, variance: 0.05 } }, GAIN_DAWNLIGHT],
  ui: { tags: ['heal', 'groupHeal', 'spender'], color: '#fff3b0' },
};

const dawnbringer: AbilityDef = {
  id: A.dawnbringer,
  name: 'Dawnbringer',
  school: 'holy',
  description:
    'Heal the 5 most injured allies within 40 yd (and smite an enemy target), gain 3 Holy Power and a Holy Shock charge, and open Avenging Light for 15 s: +20% healing and damage, Holy Shock always grants Infusion of Light, and your spenders build Dawnlight. Becomes Sunfall.',
  targeting: 'any',
  range: SPELL_RANGE,
  cooldown: { durationMs: 60000 },
  cost: [{ resource: 'mana', amount: 800 }],
  gain: [{ resource: 'holy_power', amount: 3 }],
  effects: [
    { type: 'heal', to: 'area', area: injured(40), smart: 5, amount: { coef: 2.0, variance: 0.05 } },
    { type: 'conditional', cond: { type: 'targetIsEnemy' }, then: [{ type: 'damage', school: 'holy', amount: { coef: 2.4, variance: 0.05 } }] },
    gain(1, AVENGING_LIGHT),
    gain(1, SUNFALL_READY),
    { type: 'modifyCooldown', to: 'self', abilityId: A.holyShock, addCharge: true },
  ],
  ui: { tags: ['heal', 'cooldown'], color: '#ffd166' },
};

const sunfall: AbilityDef = {
  id: A.sunfall,
  name: 'Sunfall',
  school: 'holy',
  description:
    'Consume all Dawnlight: heal the 5 most injured allies within 40 yd, +50% per Dawnlight. On an enemy, the same amount strikes it and up to 4 enemies near it.',
  targeting: 'any',
  range: SPELL_RANGE,
  usableIf: { type: 'selfHasAura', auraId: AURA.dawnlight },
  effects: [
    { type: 'heal', to: 'area', area: injured(40), smart: 5, amount: { coef: 1.0, variance: 0.05, perStack: { auraId: AURA.dawnlight, on: 'self', mult: 0.5 } } },
    {
      type: 'conditional',
      cond: { type: 'targetIsEnemy' },
      then: [
        {
          type: 'damage',
          school: 'holy',
          to: 'area',
          area: { anchor: 'target', shape: { type: 'circle', radius: 8 }, affects: 'enemies', maxTargets: 5 },
          amount: { coef: 1.0, variance: 0.05, perStack: { auraId: AURA.dawnlight, on: 'self', mult: 0.5 } },
        },
      ],
    },
    { type: 'removeAura', to: 'self', auraId: AURA.dawnlight },
    { type: 'removeAura', to: 'self', auraId: AURA.sunfallReady },
  ],
  ui: { tags: ['heal', 'groupHeal', 'spender'], color: '#ffb347', animAs: A.dawnbringer },
};

const radiantWave: AbilityDef = {
  id: A.radiantWave,
  name: 'Radiant Wave',
  school: 'holy',
  description: 'Heal every ally within 40 yd and leave a 6 s heal over time. Grants Infusion of Light and 1 Holy Power (in Avenging Light, +1 Dawnlight).',
  targeting: 'none',
  cooldown: { durationMs: 30000 },
  cost: [{ resource: 'mana', amount: 1500 }],
  effects: [
    { type: 'heal', to: 'area', area: { anchor: 'caster', shape: { type: 'circle', radius: 40 }, affects: 'allies' }, amount: { coef: 2.5, variance: 0.05 } },
    {
      type: 'applyAura',
      to: 'area',
      area: { anchor: 'caster', shape: { type: 'circle', radius: 40 }, affects: 'allies' },
      aura: {
        id: AURA.radiantWaveHot,
        name: 'Radiant Wave',
        kind: 'buff',
        durationMs: 6000,
        stacking: 'refresh',
        pandemic: true,
        periodic: { intervalMs: 1000, hasteScaled: true, effects: [{ type: 'heal', amount: { coef: 0.5 } }] },
      },
    },
    gain(1, INFUSION_OF_LIGHT),
    holyPower(1),
    GAIN_DAWNLIGHT,
  ],
  ui: { tags: ['groupHeal', 'heal', 'cooldown'], color: '#fff3b0' },
};

const guardiansAegis: AbilityDef = {
  id: A.guardiansAegis,
  name: "Guardian's Aegis",
  school: 'holy',
  description: 'Reduce an ally’s damage taken by 40% for 8 s.',
  targeting: 'ally',
  range: SPELL_RANGE,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 90000 },
  effects: [allyBuff(AURA.guardiansAegis, "Guardian's Aegis", 8000, [{ stat: 'damageTakenPct', value: -0.4 }])],
  ui: { tags: ['externalDefensive', 'cooldown'], color: '#ffd166' },
};

const cleanse: AbilityDef = {
  id: A.cleanse,
  name: 'Cleanse',
  school: 'holy',
  description: 'Remove one Magic or Poison effect from an ally.',
  targeting: 'ally',
  range: SPELL_RANGE,
  cooldown: { durationMs: 8000 },
  cost: [{ resource: 'mana', amount: 300 }],
  effects: [{ type: 'dispel', dispelTypes: ['magic', 'poison'], harmful: true, count: 1 }],
  ui: { tags: ['dispel'], color: '#9be7ff' },
};

const divineShield: AbilityDef = {
  id: A.divineShield,
  name: 'Divine Shield',
  school: 'holy',
  description: 'Immune to all damage and control effects for 8 s.',
  targeting: 'self',
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 300000 },
  effects: [selfBuff(AURA.divineShield, 'Divine Shield', 8000, [], { flags: { immuneDamage: true, immuneCc: true } })],
  ui: { tags: ['majorDefensive'], color: '#fff3b0' },
};

const blessingOfFreedom: AbilityDef = {
  id: A.blessingOfFreedom,
  name: 'Blessing of Freedom',
  school: 'holy',
  description: 'An ally is immune to control effects for 6 s.',
  targeting: 'ally',
  range: SPELL_RANGE,
  gcdMs: OFF_GCD,
  cooldown: { durationMs: 25000 },
  effects: [allyBuff('lightbinder.blessing_of_freedom', 'Blessing of Freedom', 6000, [], { flags: { immuneCc: true } })],
  ui: { tags: ['movement'], color: '#9be7ff' },
};

export const lightbinder: ClassDef = {
  id: CLASS.lightbinder,
  name: 'Lightbinder',
  role: 'healer',
  resources: [
    { id: 'mana', name: 'Mana', max: 10000, start: 10000, regenPerSec: 80, regenPerSecOoc: 600 },
    { id: 'holy_power', name: 'Holy Power', max: 5, start: 0 },
  ],
  binds: {
    '1': A.flashHeal,
    '2': A.holyShock,
    '3': A.wordOfLight,
    '4': A.judgment,
    Q: A.radiantWave,
    E: A.guardiansAegis,
    R: A.cleanse,
    F: A.divineShield,
  },
  extraBinds: { '5': A.dawnsong, '6': A.dawnbringer, G: A.blessingOfFreedom },
  abilities: [flashHeal, holyShock, wordOfLight, judgment, dawnsong, dawnbringer, sunfall, radiantWave, guardiansAegis, cleanse, divineShield, blessingOfFreedom, dodge],
  // Blessing of Freedom stays first (the web client's G fallback reads extraAbilities[0]). Keys 5/6 and
  // the Sunfall transform are listed too so validators that only know the 8 base binds accept them.
  extraAbilities: [A.blessingOfFreedom, A.dawnsong, A.dawnbringer, A.sunfall, ABILITY.common.dodge],
  passives: [lightEngine()],
  baseStats: baseStats({ armor: 0.1 }),
  maxHp: PLAYER_HP.healer,
  moveSpeed: 7,
  melee: null,
  color: '#f0d050',
  // +field: hero-level ability gates (unlocks.ts).
  unlocks: LIGHTBINDER_UNLOCKS,
};
